import type { AdyenPaymentResponse } from '@/scopes/payment/infrastructure/types'

const ERROR_PREFIX = '[AdyenPayment]'

export const ADYEN_TELEMETRY_PLATFORM = 'next-app'

export const AdyenTelemetryEventValues = {
  ACTION_HANDLED: 'Payment.ActionHandled',
  CARD_FIELD_INVALID: 'Payment.CardFieldInvalid',
  SUBMIT_BLOCKED_BY_VALIDATION: 'Payment.SubmitBlockedByValidation',
} as const

export type AdyenTelemetryEvent = (typeof AdyenTelemetryEventValues)[keyof typeof AdyenTelemetryEventValues]

export const AdyenErrorCodeValues = {
  COMPONENT_ERROR: 'ComponentError',
  PAYMENT_FAILED: 'PaymentFailed',
  PAYMENT_SUBMISSION_EXCEPTION: 'PaymentSubmissionException',
  ADDITIONAL_DETAILS_SUBMISSION_EXCEPTION: 'AdditionalDetailsSubmissionException',
  PAYMENT_MOUNT_EXCEPTION: 'PaymentMountException',
  INVALID_SUCCESS_URL: 'InvalidSuccessUrl',
  UNSUPPORTED_COMPONENT_TYPE: 'UnsupportedComponentType',
  UNKNOWN_ERROR: 'UnknownError',
} as const

export type AdyenErrorCode = (typeof AdyenErrorCodeValues)[keyof typeof AdyenErrorCodeValues]

export const UNSUPPORTED_ADYEN_COMPONENT_TYPE_ERROR_NAME = 'UnsupportedAdyenComponentTypeError'
export const INVALID_SUCCESS_URL_ERROR_NAME = 'InvalidAdyenSuccessUrlError'

export interface AdyenTelemetryValues {
  bookingNumber: string
  paymentMethodBrand: string
  paymentOption: string
  pspReference?: string
  merchantReference?: string
}

export interface AdyenReferences {
  pspReference?: string
  merchantReference?: string
}

export interface AdyenPaymentFailureDetails {
  resultCode?: string
  refusalReason?: string
  refusalReasonCode?: string
}

export function extractAdyenPaymentFailureDetails(data: unknown): AdyenPaymentFailureDetails {
  if (typeof data !== 'object' || data === null) {
    return {}
  }

  return removeUndefinedValues({
    resultCode: 'resultCode' in data && typeof data.resultCode === 'string' ? data.resultCode : undefined,
    refusalReason: 'refusalReason' in data && typeof data.refusalReason === 'string' ? data.refusalReason : undefined,
    refusalReasonCode: 'refusalReasonCode' in data && typeof data.refusalReasonCode === 'string' ? data.refusalReasonCode : undefined,
  })
}

export function describeAdyenPaymentFailure(data: unknown): string {
  const details = extractAdyenPaymentFailureDetails(data)

  return Object.entries(details).map(([key, value]) => `${key}=${value}`).join(' ') || 'Unknown Adyen error'
}

export function buildAdyenReportedError(error: unknown, errorCode: AdyenErrorCode = AdyenErrorCodeValues.UNKNOWN_ERROR) {
  const message = getErrorMessage(error)
  const original = error instanceof Error ? error : new Error(message)
  const reported = new Error(`${ERROR_PREFIX}[${errorCode}] ${message}`)
  reported.name = `AdyenPaymentError:${errorCode}`
  reported.stack = original.stack

  return reported
}

export function resolveAdyenErrorCode(error: unknown, fallbackCode: AdyenErrorCode): AdyenErrorCode {
  if (error instanceof Error && error.name === UNSUPPORTED_ADYEN_COMPONENT_TYPE_ERROR_NAME) {
    return AdyenErrorCodeValues.UNSUPPORTED_COMPONENT_TYPE
  }

  if (error instanceof Error && error.name === INVALID_SUCCESS_URL_ERROR_NAME) {
    return AdyenErrorCodeValues.INVALID_SUCCESS_URL
  }

  return fallbackCode
}

export function buildAdyenTelemetryInfo({
  bookingNumber,
  paymentMethodBrand,
  paymentOption,
  pspReference,
  merchantReference,
}: AdyenTelemetryValues) {
  return removeUndefinedValues({
    platform: ADYEN_TELEMETRY_PLATFORM,
    adyenBookingNumber: bookingNumber,
    adyenPaymentMethodBrand: paymentMethodBrand,
    adyenPaymentOption: paymentOption,
    adyenPspReference: pspReference,
    adyenMerchantReference: merchantReference,
  })
}

export function extractAdyenReferences(response: AdyenPaymentResponse): AdyenReferences {
  return removeUndefinedValues({
    pspReference: response.pspReference ?? response.additionalData?.pspReference,
    merchantReference: response.merchantReference ?? response.additionalData?.merchantReference,
  })
}

function getErrorMessage(error: unknown) {
  if (error === null || error === undefined) {
    return 'Unknown Adyen error'
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'object' && error !== null) {
    if ('message' in error && typeof error.message === 'string') {
      return error.message
    }

    return describeAdyenPaymentFailure(error)
  }

  return String(error)
}

function removeUndefinedValues<T extends Record<string, unknown>>(values: T) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  ) as { [K in keyof T as undefined extends T[K] ? never : K]: T[K] } & Partial<T>
}
